import { useMemo } from "react";
import {
  Sparkles,
  TrendingUp,
  Users2,
  Wallet,
  ArrowRight,
  Target,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { getMockData } from "../lib/mockData";
import { loadSettings } from "../lib/settings";
import { formatPct, formatCurrency } from "../lib/csv";

export default function Insights() {
  const data = useMemo(() => getMockData(), []);
  const settings = useMemo(() => loadSettings(), []);
  const { kpis, insights, history } = data;

  // Recompute estimated savings using the user-configurable cost per desk.
  const costPerDesk = Number(settings.costPerDesk) || 6000;
  const currency = settings.currency || "EUR";
  const estimatedSavings = insights.desksToReduce * costPerDesk;

  // Peak vs low days from history.
  const sorted = [...history].sort((a, b) => a.occupancyRate - b.occupancyRate);
  const lowest = sorted.slice(0, 5);
  const highest = sorted.slice(-5).reverse();

  return (
    <div data-testid="page-insights">
      <PageHeader
        title="Insights & recommendations"
        subtitle="Translate 60 days of presence data into concrete decisions."
        testId="insights-header"
      />

      {/* Headline savings */}
      <Card
        className="mb-8 overflow-hidden border-slate-200 shadow-sm"
        data-testid="card-savings"
      >
        <div className="grid grid-cols-1 lg:grid-cols-3">
          <div className="bg-blue-600 p-8 text-white lg:col-span-1">
            <div className="flex items-center gap-2 text-blue-100">
              <Wallet className="h-4 w-4" />
              <span className="text-xs font-medium uppercase tracking-wide">
                Estimated annual savings
              </span>
            </div>
            <div className="mt-4 font-heading text-5xl font-bold tracking-tight">
              {formatCurrency(estimatedSavings, currency)}
            </div>
            <p className="mt-3 text-sm text-blue-100">
              Based on reducing {insights.desksToReduce} desks at{" "}
              {formatCurrency(costPerDesk, currency)} fully loaded annual cost
              per desk.
              <br />
              <span className="text-blue-200">
                Adjust the cost in Settings to recalculate.
              </span>
            </p>
          </div>
          <div className="grid grid-cols-1 gap-px bg-slate-100 lg:col-span-2 lg:grid-cols-3">
            <div className="bg-white p-6">
              <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Unused desks
              </div>
              <div className="mt-2 font-heading text-3xl font-semibold text-slate-900">
                {formatPct(insights.unusedDesksPct)}
              </div>
              <p className="mt-1 text-xs text-slate-500">
                of allocated capacity sits idle on average.
              </p>
            </div>
            <div className="bg-white p-6">
              <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Wasted room capacity
              </div>
              <div className="mt-2 font-heading text-3xl font-semibold text-slate-900">
                {formatPct(insights.wastedRoomCapacity)}
              </div>
              <p className="mt-1 text-xs text-slate-500">
                meeting rooms run far below capacity.
              </p>
            </div>
            <div className="bg-white p-6">
              <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Recommended desk reduction
              </div>
              <div className="mt-2 font-heading text-3xl font-semibold text-slate-900">
                {insights.reduceDesksPct}%
              </div>
              <p className="mt-1 text-xs text-slate-500">
                ≈ {insights.desksToReduce} workplaces
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Key findings */}
      <h2 className="mb-4 font-heading text-xl font-semibold tracking-tight text-slate-900">
        Key findings
      </h2>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="findings-grid">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-blue-50 text-blue-600">
              <TrendingUp className="h-4 w-4" />
            </div>
            <div className="mt-3 text-sm font-semibold text-slate-900">
              Peak day
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {highest[0].date} · {formatPct(highest[0].occupancyRate)} occupancy
            </p>
            <ul className="mt-4 space-y-1 text-xs text-slate-600">
              {highest.slice(0, 3).map((d) => (
                <li key={d.date} className="flex justify-between">
                  <span>{d.date}</span>
                  <span className="font-medium text-slate-900">
                    {formatPct(d.occupancyRate)}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-rose-50 text-rose-600">
              <Target className="h-4 w-4" />
            </div>
            <div className="mt-3 text-sm font-semibold text-slate-900">
              Quietest days
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {lowest[0].date} · {formatPct(lowest[0].occupancyRate)} occupancy
            </p>
            <ul className="mt-4 space-y-1 text-xs text-slate-600">
              {lowest.slice(0, 3).map((d) => (
                <li key={d.date} className="flex justify-between">
                  <span>{d.date}</span>
                  <span className="font-medium text-slate-900">
                    {formatPct(d.occupancyRate)}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-emerald-50 text-emerald-600">
              <Users2 className="h-4 w-4" />
            </div>
            <div className="mt-3 text-sm font-semibold text-slate-900">
              Department gap
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Highest vs lowest department
            </p>
            <div className="mt-4 space-y-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">{insights.peakDept.department}</span>
                <Badge variant="secondary" className="bg-emerald-50 text-emerald-700">
                  {formatPct(insights.peakDept.occupancyRate)}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">{insights.lowDept.department}</span>
                <Badge variant="secondary" className="bg-rose-50 text-rose-700">
                  {formatPct(insights.lowDept.occupancyRate)}
                </Badge>
              </div>
              <p className="pt-2 text-slate-500">
                Δ {formatPct(insights.peakDept.occupancyRate - insights.lowDept.occupancyRate)} variance
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recommendations */}
      <h2 className="mb-4 mt-10 font-heading text-xl font-semibold tracking-tight text-slate-900">
        Recommendations
      </h2>
      <Card className="border-slate-200 shadow-sm" data-testid="recommendations">
        <CardContent className="divide-y divide-slate-100 p-0">
          <Recommendation
            title={`Reduce desks by ${insights.reduceDesksPct}%`}
            body={`Consolidate ${insights.desksToReduce} workplaces and reallocate the area to focus rooms or hospitality. Headroom of 20% preserved for peak Tuesdays.`}
            badge="High impact"
            badgeTone="bg-blue-50 text-blue-700"
          />
          <Recommendation
            title="Rebalance departments"
            body={`Shift ${insights.peakDept.department} bench overflow toward ${insights.lowDept.department}'s zone. Reduce competition for desks on peak days while keeping team adjacency.`}
            badge="Medium impact"
            badgeTone="bg-amber-50 text-amber-700"
          />
          <Recommendation
            title="Optimize meeting rooms"
            body={`${insights.underusedRooms.length} rooms run below 50% capacity. Convert one large room per floor into two focus pods and enable auto-release on 10-min no-show.`}
            badge="Quick win"
            badgeTone="bg-emerald-50 text-emerald-700"
          />
        </CardContent>
      </Card>

      <div className="mt-6 flex items-center gap-2 rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm" data-testid="insight-footer">
        <Sparkles className="h-4 w-4 text-blue-600" />
        <span>
          KPI snapshot · desk occupancy {formatPct(kpis.deskOccupancyRate)} · room
          efficiency {formatPct(kpis.meetingEfficiency)} · no-show{" "}
          {formatPct(kpis.noShowRate)}.
        </span>
      </div>
    </div>
  );
}

function Recommendation({ title, body, badge, badgeTone }) {
  return (
    <div className="flex flex-col gap-3 p-6 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex-1">
        <div className="flex items-center gap-3">
          <ArrowRight className="h-4 w-4 text-blue-600" />
          <h3 className="font-heading text-base font-semibold text-slate-900">
            {title}
          </h3>
        </div>
        <p className="ml-7 mt-1 text-sm text-slate-600">{body}</p>
      </div>
      <Badge variant="secondary" className={badgeTone}>
        {badge}
      </Badge>
    </div>
  );
}
